import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Leaf, Sparkles, Users } from 'lucide-react';
import { brand, services, values, clients, testimonials, images } from '../mock';

const Home = () => {
  return (
    <main className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">
                <Leaf className="w-3.5 h-3.5" /> Leadership · Coaching · Consulting
              </div>
              <h1 className="font-serif-display text-[52px] md:text-[86px] leading-[0.98] text-[#1E3A2B] mt-6 tracking-[-0.02em]">
                Changing<br />
                <span className="italic text-[#6B8E4E]">perspective</span>,<br />
                guiding insight.
              </h1>
              <p className="mt-8 max-w-xl text-[17px] text-[#3d4a41] leading-relaxed">
                Most culture and leadership programmes fail for a predictable reason: they ask people to think
                differently, without changing what makes them act differently. We look at what&rsquo;s really
                shaping behaviour for individuals, teams, and the organisations around them, and help shift it,
                so change actually holds.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/services"
                  className="btn-transition inline-flex items-center gap-2 bg-[#1E3A2B] hover:bg-[#152B20] text-[#FAF6EE] px-6 py-3.5 rounded-full text-[14px]">
                  Explore what we do <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/contact"
                  className="btn-transition inline-flex items-center gap-2 border border-[#1E3A2B] text-[#1E3A2B] hover:bg-[#1E3A2B] hover:text-[#FAF6EE] px-6 py-3.5 rounded-full text-[14px]">
                  Talk to us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative">
                <div className="aspect-square rounded-[28px] overflow-hidden">
                  <img src={images.hero} alt="Byron Batten presenting" className="w-full h-full object-cover image-zoom" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STRAP QUOTE */}
      <section className="relative py-24 md:py-32 bg-[#1E3A2B] text-[#EEE7D3] overflow-hidden grain">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 relative">
          <div className="text-[11px] tracking-[0.24em] uppercase text-[#B8CE7F]">Our belief</div>
          <h2 className="font-serif-display text-4xl md:text-7xl leading-[1.02] mt-6 reveal">
            {brand.strap.split(' but ')[0]}<br />
            <span className="italic text-[#B8CE7F]">but what you see.</span>
          </h2>
          <p className="mt-8 max-w-2xl text-[#D5CDB8] text-lg reveal">
            Seeing differently is where change starts, but seeing isn&rsquo;t where it ends.
          </p>
          <p className="mt-5 max-w-2xl text-[#D5CDB8] text-lg reveal">
            Byron Batten Consultancy works with leaders, teams and organisations across NHS, local authority
            and private sector, through coaching, consultancy and keynote speaking to close the gap between
            what people see and what they do, building the trust, accountability and behaviour that sustain change.
          </p>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">What we do</div>
              <h2 className="font-serif-display text-5xl md:text-6xl text-[#1E3A2B] mt-4 max-w-2xl leading-[1.02]">
                Three ways in.
              </h2>
              <p className="mt-5 max-w-none text-[16px] text-[#3d4a41] leading-relaxed">
                Coaching, consulting, and keynote speaking: three routes to the same behavioural approach.
              </p>
            </div>
            <Link to="/services" className="link-underline text-[#1E3A2B] text-sm inline-flex items-center gap-1">
              View all services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, idx) => (
              <Link
                to={`/services#${s.id}`}
                key={s.id}
                className="reveal group relative rounded-3xl border border-[#E7DFCF] p-8 bg-[#FDFAF2] hover:bg-[#F3EFDF] btn-transition"
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#6B8E4E]">{s.kicker}</div>
                <h3 className="font-serif-display text-[32px] leading-tight text-[#1E3A2B] mt-6">{s.title}</h3>
                <p className="text-[13px] text-[#6B8E4E] mt-1">{s.subtitle}</p>
                <p className="mt-6 text-[15px] text-[#3d4a41] leading-relaxed whitespace-pre-line">{s.body}</p>
                <div className="mt-10 inline-flex items-center gap-2 text-[#1E3A2B] text-sm">
                  Learn more
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 btn-transition" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-[#F3EFDF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">Our values</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#1E3A2B] mt-4 leading-[1.02]">
              The behaviours behind<br /> the behaviour change.
            </h2>
            <p className="mt-6 max-w-md text-[#3d4a41]">
              Three core values shape every conversation, every workshop and every hour of our coaching offer.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 link-underline text-[#1E3A2B]">
              Meet the practice <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-7 grid gap-4">
            {values.map((v, i) => (
              <div key={v.key} className="reveal bg-[#FAF6EE] rounded-2xl p-8 border border-[#E7DFCF] flex gap-6" style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="shrink-0 w-12 h-12 rounded-full bg-[#1E3A2B] text-[#B8CE7F] flex items-center justify-center">
                  {i === 0 && <Sparkles className="w-5 h-5" />}
                  {i === 1 && <Leaf className="w-5 h-5" />}
                  {i === 2 && <Users className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="font-serif-display text-2xl text-[#1E3A2B]">{v.title}</h3>
                  <p className="mt-2 text-[15px] text-[#3d4a41] leading-relaxed">{v.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">In their words</div>
          <h2 className="font-serif-display text-4xl md:text-6xl text-[#1E3A2B] mt-4 max-w-3xl leading-[1.05]">
            The work is human, the shifts are real.
          </h2>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={t.who} className="reveal bg-[#FDFAF2] border border-[#E7DFCF] rounded-3xl p-8" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="font-serif-display text-5xl text-[#E27B47] leading-none">“</div>
                <p className="mt-4 text-[16px] text-[#1E3A2B] leading-relaxed">{t.quote}</p>
                <div className="mt-6 text-[12px] tracking-[0.18em] uppercase text-[#6B8E4E]">{t.who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-[32px] bg-[#6B8E4E] text-[#FAF6EE] p-10 md:p-20 grain">
            <div className="absolute inset-0 opacity-[0.15]" style={{
              backgroundImage: 'radial-gradient(circle at 80% 20%, #FAF6EE 0%, transparent 40%)'
            }} />
            <div className="relative grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-8">
                <div className="text-[11px] tracking-[0.24em] uppercase text-[#EEE7D3]/80">Ready when you are</div>
                <h3 className="font-serif-display text-4xl md:text-6xl mt-4 leading-[1.02]">
                  Let’s design a conversation<br /> that changes something.
                </h3>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link to="/contact"
                  className="btn-transition inline-flex items-center gap-2 bg-[#1E3A2B] hover:bg-[#152B20] text-[#FAF6EE] px-7 py-4 rounded-full text-[14px]">
                  Book a call <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
